import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ShareIcon from '@mui/icons-material/Share';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import PageShell from '../components/PageShell';
import ProductCard from '../components/ProductCard';
import StoreActionCard from '../components/StoreActionCard';
import ChainedSaleCard from '../components/ChainedSaleCard';
import request, { uploadImage } from '../api';
import { formatDateTime } from '../dateUtils';
import { sanitizeIntegerInput, formatMXN } from '../numberUtils';
import { PRODUCT_TYPES } from '../productTypes';

function StoreDashboard() {
    const [token] = useState(localStorage.getItem('storeToken'));
    const storeName = localStorage.getItem('storeName');
    const [products, setProducts] = useState([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [startingPrice, setStartingPrice] = useState('');
    const [productType, setProductType] = useState('');
    const [terminationDays, setTerminationDays] = useState('');
    const [terminationClock, setTerminationClock] = useState('');
    const [error, setError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [shareMessage, setShareMessage] = useState('');
    const [redeemOpen, setRedeemOpen] = useState(false);
    const [redeemCode, setRedeemCode] = useState('');
    const [redeemError, setRedeemError] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [chainedSales, setChainedSales] = useState([]);
    const [chainedSaleOpen, setChainedSaleOpen] = useState(false);
    const [chainedName, setChainedName] = useState('');
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [chainedSaving, setChainedSaving] = useState(false);
    const [chainedError, setChainedError] = useState('');
    const [chainedImageUrl, setChainedImageUrl] = useState('');
    const [chainedImageUploading, setChainedImageUploading] = useState(false);
    const [viewChainedSale, setViewChainedSale] = useState(null);
    const [detailImageUploading, setDetailImageUploading] = useState(false);
    const [durationInput, setDurationInput] = useState('');
    const [lobbyWaitInput, setLobbyWaitInput] = useState('');
    const [savingDuration, setSavingDuration] = useState(false);
    const [startingLive, setStartingLive] = useState(false);
    const [deletingChainedSaleId, setDeletingChainedSaleId] = useState(null);
    const [liveRoom, setLiveRoom] = useState(null);
    const navigate = useNavigate();

    const loadProducts = useCallback(() => {
        if (!token) return;
        request('/store/products', { token })
            .then(setProducts)
            .catch((err) => setError(err.message));
    }, [token]);

    const loadChainedSales = useCallback(() => {
        if (!token) return;
        request('/store/chained-sales', { token })
            .then(setChainedSales)
            .catch((err) => setError(err.message));
    }, [token]);

    useEffect(() => {
        if (!token) {
            navigate('/store/login');
            return;
        }
        loadProducts();
        loadChainedSales();
    }, [token, navigate, loadProducts, loadChainedSales]);

    const createProduct = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const [terminationHours, terminationMinutes] = terminationClock.split(':').map(Number);
            const terminationDateTime = new Date();
            terminationDateTime.setDate(terminationDateTime.getDate() + Number(terminationDays));
            terminationDateTime.setHours(terminationHours, terminationMinutes, 0, 0);

            const body = {
                title,
                description,
                image_url: imageUrl,
                starting_price: Number(startingPrice),
                starts_at: new Date().toISOString(),
                termination_time: terminationDateTime.toISOString(),
                product_type: productType,
            };
            await request('/store/products', {
                method: 'POST',
                token,
                body,
            });
            setTitle('');
            setDescription('');
            setImageUrl('');
            setStartingPrice('');
            setTerminationDays('');
            setTerminationClock('');
            setProductType('');
            setCreateOpen(false);
            loadProducts();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        setUploading(true);
        try {
            const data = await uploadImage(file, token);
            setImageUrl(data.url);
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const confirmDelete = async () => {
        const id = deleteTarget;
        setDeleteTarget(null);
        try {
            await request(`/store/products/${id}`, { method: 'DELETE', token });
            loadProducts();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleShare = async (item) => {
        const url = `${window.location.origin}/products/${item.id}`;
        try {
            await navigator.clipboard.writeText(url);
            setShareMessage('Enlace del producto copiado al portapapeles');
        } catch {
            setShareMessage(url);
        }
    };

    const logout = () => {
        localStorage.removeItem('storeToken');
        localStorage.removeItem('storeName');
        navigate('/store/login');
    };

    const handleActionStub = (label) => {
        setShareMessage(`${label}: función próximamente disponible`);
    };

    const closeRedeem = () => {
        setRedeemOpen(false);
        setRedeemCode('');
        setRedeemError('');
    };

    const confirmRedeem = async () => {
        setRedeemError('');
        setRedeeming(true);
        try {
            const product = await request('/store/redeem', {
                method: 'POST',
                token,
                body: { code: redeemCode },
            });
            closeRedeem();
            setShareMessage(`"${product.title}" marcado como entregado`);
            loadProducts();
        } catch (err) {
            setRedeemError(err.message);
        } finally {
            setRedeeming(false);
        }
    };

    const openChainedSaleModal = () => {
        setChainedName('');
        setSelectedProductIds([]);
        setChainedError('');
        setChainedImageUrl('');
        setChainedSaleOpen(true);
    };

    const closeChainedSaleModal = () => {
        setChainedSaleOpen(false);
    };

    const handleChainedImageChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setChainedError('');
        setChainedImageUploading(true);
        try {
            const data = await uploadImage(file, token);
            setChainedImageUrl(data.url);
        } catch (err) {
            setChainedError(err.message);
        } finally {
            setChainedImageUploading(false);
        }
    };

    const handleDetailImageChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        setDetailImageUploading(true);
        try {
            const data = await uploadImage(file, token);
            const updated = await request(`/store/chained-sales/${viewChainedSale.id}`, {
                method: 'PATCH',
                token,
                body: { image_url: data.url },
            });
            setViewChainedSale((prev) => (prev ? { ...prev, image_url: updated.image_url } : prev));
            loadChainedSales();
        } catch (err) {
            setError(err.message);
        } finally {
            setDetailImageUploading(false);
        }
    };

    const toggleProductSelection = (id) => {
        setSelectedProductIds((prev) =>
            prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
        );
    };

    const confirmChainedSale = async () => {
        setChainedError('');
        setChainedSaving(true);
        try {
            await request('/store/chained-sales', {
                method: 'POST',
                token,
                body: { name: chainedName.trim(), product_ids: selectedProductIds, image_url: chainedImageUrl || null },
            });
            setChainedSaleOpen(false);
            setShareMessage('Colección creada correctamente');
            loadProducts();
            loadChainedSales();
        } catch (err) {
            setChainedError(err.message);
        } finally {
            setChainedSaving(false);
        }
    };

    const openChainedSaleDetail = (collection) => {
        setViewChainedSale(collection);
        setDurationInput(collection.max_duration_minutes ? String(collection.max_duration_minutes) : '');
        setLobbyWaitInput(collection.lobby_wait_minutes ? String(collection.lobby_wait_minutes) : '');
    };

    const removeFromCollection = async (productId) => {
        try {
            await request(`/store/chained-sales/${viewChainedSale.id}/products/${productId}`, {
                method: 'DELETE',
                token,
            });
            setViewChainedSale((prev) =>
                prev ? { ...prev, products: prev.products.filter((p) => p.id !== productId) } : prev
            );
            loadProducts();
            loadChainedSales();
        } catch (err) {
            setError(err.message);
        }
    };

    const saveDuration = async () => {
        setSavingDuration(true);
        try {
            const updated = await request(`/store/chained-sales/${viewChainedSale.id}`, {
                method: 'PATCH',
                token,
                body: {
                    max_duration_minutes: Number(durationInput),
                    lobby_wait_minutes: Number(lobbyWaitInput),
                },
            });
            setViewChainedSale((prev) =>
                prev
                    ? { ...prev, max_duration_minutes: updated.max_duration_minutes, lobby_wait_minutes: updated.lobby_wait_minutes }
                    : prev
            );
            loadChainedSales();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingDuration(false);
        }
    };

    const startLiveRoom = async () => {
        setStartingLive(true);
        setError('');
        try {
            const room = await request(`/store/chained-sales/${viewChainedSale.id}/start-live`, {
                method: 'POST',
                token,
            });
            setViewChainedSale(null);
            setLiveRoom(room);
            loadProducts();
            loadChainedSales();
        } catch (err) {
            setError(err.message);
        } finally {
            setStartingLive(false);
        }
    };

    const deleteChainedSale = async (id) => {
        setDeletingChainedSaleId(id);
        setError('');
        try {
            await request(`/store/chained-sales/${id}`, {
                method: 'DELETE',
                token,
            });
            setViewChainedSale((prev) => (prev && prev.id === id ? null : prev));
            loadChainedSales();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeletingChainedSaleId(null);
        }
    };

    const quickActions = [
        {
            key: 'redeem',
            label: 'Canjear Subasta',
            description: 'Ingresa el código del cliente para validar la subasta',
            icon: <LocalAtmOutlinedIcon />,
        },
        {
            key: 'chained-sale',
            label: 'Ventas en Vivo',
            description: 'Agrega varios items, se subastarán 1 a 1 en una sesión en vivo.',
            icon: <LinkOutlinedIcon />,
        },
        {
            key: 'flash-sale',
            label: 'Venta Normal',
            description: 'Publica un item, y que el tiempo decida su destino',
            icon: <BoltOutlinedIcon />,
        },
        // {
        //     key: 'publish-item',
        //     label: 'Publica un Item',
        //     description: 'Crea un ítem para después ser subastado',
        //     icon: <AddCardOutlinedIcon />,
        // },
    ];

    const nav = (
        <>
            <Button component={RouterLink} to="/store/stats" color="inherit">Estadísticas</Button>
            <Button component={RouterLink} to="/store/settings" color="inherit">Datos de Tienda</Button>
            <Button component={RouterLink} to="/auctions" color="inherit">Ver Subastas</Button>
            <Button onClick={logout} variant="outlined" color="inherit">Cerrar Sesión</Button>
        </>
    );

    return (
        <PageShell title={`Panel de Tienda${storeName ? `: ${storeName}` : ''}`} nav={nav}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                useFlexGap
                flexWrap="wrap"
                sx={{ mb: { xs: 4, sm: 5 } }}
            >
                {quickActions.map((action) => (
                    <StoreActionCard
                        key={action.key}
                        icon={action.icon}
                        label={action.label}
                        description={action.description}
                        onClick={() => {
                            if (action.key === 'redeem') return setRedeemOpen(true);
                            if (action.key === 'chained-sale') return openChainedSaleModal();
                            if (action.key === 'flash-sale') return setCreateOpen(true);
                            return handleActionStub(action.label);
                        }}
                    />
                ))}
            </Stack>

            <Grid container spacing={3} sx={{ mb: { xs: 10, sm: 12 } }}>
                {chainedSales.map((collection) => (
                    <Grid key={`chained-${collection.id}`} item xs={12} sm={6} md={4}>
                        <ChainedSaleCard
                            name={collection.name}
                            count={collection.products.length}
                            image={collection.image_url}
                            onClick={() => openChainedSaleDetail(collection)}
                            actions={
                                collection.products.length === 0 && (
                                    <Button
                                        color="error"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => deleteChainedSale(collection.id)}
                                        disabled={deletingChainedSaleId === collection.id}
                                    >
                                        {deletingChainedSaleId === collection.id ? 'Quitando…' : 'Quitar'}
                                    </Button>
                                )
                            }
                        />
                    </Grid>
                ))}
                {products.filter((item) => !item.chained_sale_id).map((item) => (
                    <Grid key={item.id} item xs={12} sm={6} md={4}>
                        <ProductCard
                            image={item.image_url}
                            title={item.title}
                            subtitle={item.description}
                            meta={
                                <>
                                    <Typography variant="body2" gutterBottom>
                                        Precio Inicial: {formatMXN(item.starting_price)}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Puja Actual: {item.current_bid != null ? formatMXN(item.current_bid) : '—'}
                                        {item.current_bid_username && ` de ${item.current_bid_username}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Finaliza: {formatDateTime(item.termination_time)}
                                    </Typography>
                                    {item.status === 'on_auction' && (
                                        <Chip size="small" color="warning" label="En subasta en vivo" sx={{ mt: 0.5 }} />
                                    )}
                                    {item.status === 'ready_to_deliver' && (
                                        <Chip
                                            size="small"
                                            color="success"
                                            label="Ganado — pendiente de entrega"
                                            sx={{ mt: 0.5 }}
                                        />
                                    )}
                                    {item.status === 'delivered' && (
                                        <Chip size="small" color="default" label="Entregado" sx={{ mt: 0.5 }} />
                                    )}
                                </>
                            }
                            actions={
                                <>
                                    <Button
                                        startIcon={<ShareIcon />}
                                        onClick={() => handleShare(item)}
                                    >
                                        Compartir
                                    </Button>
                                    <Button
                                        color="error"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => setDeleteTarget(item.id)}
                                        disabled={['ready_to_deliver', 'delivered'].includes(item.status)}
                                    >
                                        Eliminar
                                    </Button>
                                </>
                            }
                        />
                    </Grid>
                ))}
                {products.length === 0 && chainedSales.length === 0 && (
                    <Grid item xs={12}>
                        <Typography color="text.secondary" align="center">Aún no has publicado ningún producto.</Typography>
                    </Grid>
                )}
            </Grid>

            <Fab
                variant="extended"
                color="primary"
                onClick={() => setCreateOpen(true)}
                sx={{
                    position: 'fixed',
                    bottom: { xs: 24, sm: 32 },
                    right: { xs: 24, sm: 32 },
                    zIndex: (theme) => theme.zIndex.speedDial,
                }}
            >
                <AddIcon sx={{ mr: 1 }} />
                Agregar Producto
            </Fab>

            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Publicar un Nuevo Producto</DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={createProduct} sx={{ pt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField label="Título" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Tipo de Producto"
                                    value={productType}
                                    onChange={(e) => setProductType(e.target.value)}
                                    fullWidth
                                    required
                                >
                                    {PRODUCT_TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Descripción"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={uploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                                        disabled={uploading}
                                    >
                                        {uploading ? 'Subiendo…' : imageUrl ? 'Cambiar Foto' : 'Subir Foto'}
                                        <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                                    </Button>
                                    {imageUrl && (
                                        <Box sx={{ position: 'relative' }}>
                                            <Box
                                                component="img"
                                                src={imageUrl}
                                                alt="Vista previa del producto"
                                                sx={{ width: 64, height: 64, borderRadius: 2, objectFit: 'cover', display: 'block' }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => setImageUrl('')}
                                                aria-label="Quitar foto"
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid rgba(15,23,42,0.12)',
                                                    '&:hover': { bgcolor: 'background.paper' },
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Precio Inicial (MXN)"
                                    type="number"
                                    inputProps={{ step: '1', min: '1', inputMode: 'numeric' }}
                                    value={startingPrice}
                                    onChange={(e) => setStartingPrice(sanitizeIntegerInput(e.target.value))}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    Fin de la subasta
                                </Typography>
                                <Stack direction="row" spacing={1.5}>
                                    <TextField
                                        label="Cuántos Días"
                                        type="number"
                                        inputProps={{ step: '1', min: '1', max: '5', inputMode: 'numeric' }}
                                        value={terminationDays}
                                        onChange={(e) => {
                                            const digits = sanitizeIntegerInput(e.target.value);
                                            setTerminationDays(digits === '' ? '' : String(Math.min(Number(digits), 5)));
                                        }}
                                        required
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        label="Hora"
                                        type="time"
                                        value={terminationClock}
                                        onChange={(e) => setTerminationClock(e.target.value)}
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ flex: 1 }}
                                    />
                                </Stack>
                            </Grid>
                            {error && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{error}</Alert>
                                </Grid>
                            )}
                        </Grid>
                        <DialogActions sx={{ px: 0, pb: 0, pt: 3 }}>
                            <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
                            <Button type="submit" variant="contained" size="large" disabled={uploading}>
                                Crear Producto
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>¿Eliminar este producto?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">Esta acción no se puede deshacer.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={redeemOpen} onClose={closeRedeem} fullWidth maxWidth="xs">
                <DialogTitle>Canjear Subasta</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Ingresa el código de 4 dígitos del cliente para validar la entrega.
                    </Typography>
                    <TextField
                        label="Código"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(sanitizeIntegerInput(e.target.value).slice(0, 4))}
                        fullWidth
                        autoFocus
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            maxLength: 4,
                            style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
                        }}
                    />
                    <Button
                        onClick={confirmRedeem}
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={redeemCode.length !== 4 || redeeming}
                        sx={{ mt: 3 }}
                    >
                        {redeeming ? 'Validando…' : 'Confirmar'}
                    </Button>
                    {redeemError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{redeemError}</Alert>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={chainedSaleOpen} onClose={closeChainedSaleModal} fullWidth maxWidth="sm">
                <DialogTitle>Ventas en Vivo</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre de la colección"
                        value={chainedName}
                        onChange={(e) => setChainedName(e.target.value)}
                        fullWidth
                        autoFocus
                        required
                        sx={{ mt: 1, mb: 2.5 }}
                    />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Foto de la colección
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Se mostrará en la pantalla de espera mientras comienza la subasta.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={chainedImageUploading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                            disabled={chainedImageUploading}
                        >
                            {chainedImageUploading ? 'Subiendo…' : chainedImageUrl ? 'Cambiar Foto' : 'Subir Foto'}
                            <input type="file" accept="image/*" hidden onChange={handleChainedImageChange} />
                        </Button>
                        {chainedImageUrl && (
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    component="img"
                                    src={chainedImageUrl}
                                    alt="Vista previa de la colección"
                                    sx={{ width: 64, height: 64, borderRadius: 2, objectFit: 'cover', display: 'block' }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => setChainedImageUrl('')}
                                    aria-label="Quitar foto"
                                    sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        bgcolor: 'background.paper',
                                        border: '1px solid rgba(15,23,42,0.12)',
                                        '&:hover': { bgcolor: 'background.paper' },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Box>
                        )}
                    </Stack>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selecciona los productos
                    </Typography>
                    {products.filter((item) => !item.chained_sale_id && ['draft', 'ready'].includes(item.status)).length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No tienes publicaciones disponibles para agregar a una colección.
                        </Typography>
                    ) : (
                        <List disablePadding sx={{ maxHeight: 320, overflowY: 'auto' }}>
                            {products.filter((item) => !item.chained_sale_id && ['draft', 'ready'].includes(item.status)).map((item) => {
                                const checked = selectedProductIds.includes(item.id);
                                return (
                                    <ListItemButton
                                        key={item.id}
                                        onClick={() => toggleProductSelection(item.id)}
                                        selected={checked}
                                        sx={{ borderRadius: 1.5, mb: 0.5 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemAvatar>
                                            {item.image_url ? (
                                                <Avatar variant="rounded" src={item.image_url} alt={item.title} />
                                            ) : (
                                                <Avatar variant="rounded">{item.title.charAt(0)}</Avatar>
                                            )}
                                        </ListItemAvatar>
                                        <ListItemText primary={item.title} sx={{ ml: 1.5 }} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                    {chainedError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{chainedError}</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={closeChainedSaleModal}>Cancelar</Button>
                    <Button
                        onClick={confirmChainedSale}
                        variant="contained"
                        disabled={!chainedName.trim() || selectedProductIds.length === 0 || chainedSaving}
                    >
                        {chainedSaving ? 'Guardando…' : 'Confirmar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!viewChainedSale} onClose={() => setViewChainedSale(null)} fullWidth maxWidth="xs">
                <DialogTitle>{viewChainedSale?.name}</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Foto de la colección
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5 }}>
                        {viewChainedSale?.image_url && (
                            <Box
                                component="img"
                                src={viewChainedSale.image_url}
                                alt="Foto de la colección"
                                sx={{ width: 64, height: 64, borderRadius: 2, objectFit: 'cover', display: 'block' }}
                            />
                        )}
                        <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            startIcon={detailImageUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                            disabled={detailImageUploading}
                        >
                            {detailImageUploading ? 'Subiendo…' : viewChainedSale?.image_url ? 'Cambiar Foto' : 'Subir Foto'}
                            <input type="file" accept="image/*" hidden onChange={handleDetailImageChange} />
                        </Button>
                    </Stack>
                    {viewChainedSale && viewChainedSale.products.length === 0 ? (
                        <Typography color="text.secondary">Esta colección aún no tiene productos.</Typography>
                    ) : (
                        <List disablePadding>
                            {viewChainedSale?.products.map((item) => (
                                <ListItem
                                    key={item.id}
                                    sx={{ py: 0.75, pr: 0 }}
                                    secondaryAction={
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => removeFromCollection(item.id)}
                                        >
                                            Quitar
                                        </Button>
                                    }
                                >
                                    <ListItemAvatar>
                                        {item.image_url ? (
                                            <Avatar variant="rounded" src={item.image_url} alt={item.title} />
                                        ) : (
                                            <Avatar variant="rounded">{item.title.charAt(0)}</Avatar>
                                        )}
                                    </ListItemAvatar>
                                    <ListItemText primary={item.title} sx={{ ml: 1.5, mr: 8 }} />
                                </ListItem>
                            ))}
                        </List>
                    )}

                    <Stack spacing={2} sx={{ mt: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                Ingresa el tiempo máximo de cada publicación
                            </Typography>
                            <TextField
                                type="number"
                                value={durationInput}
                                onChange={(e) => setDurationInput(sanitizeIntegerInput(e.target.value))}
                                inputProps={{ min: 1, max: 15, step: 1, inputMode: 'numeric' }}
                                sx={{ width: 90 }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                Tiempo de Espera en Lobby
                            </Typography>
                            <TextField
                                type="number"
                                value={lobbyWaitInput}
                                onChange={(e) => setLobbyWaitInput(sanitizeIntegerInput(e.target.value))}
                                inputProps={{ min: 1, max: 5, step: 1, inputMode: 'numeric' }}
                                sx={{ width: 90 }}
                            />
                        </Stack>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={saveDuration}
                            disabled={
                                !durationInput ||
                                Number(durationInput) < 1 ||
                                Number(durationInput) > 15 ||
                                !lobbyWaitInput ||
                                Number(lobbyWaitInput) < 1 ||
                                Number(lobbyWaitInput) > 5 ||
                                savingDuration
                            }
                            sx={{ alignSelf: 'flex-end' }}
                        >
                            {savingDuration ? 'Guardando…' : 'Guardar'}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
                    <Button onClick={() => setViewChainedSale(null)}>Cerrar</Button>
                    {viewChainedSale?.products.length === 0 ? (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => deleteChainedSale(viewChainedSale.id)}
                            disabled={deletingChainedSaleId === viewChainedSale.id}
                        >
                            {deletingChainedSaleId === viewChainedSale.id ? 'Quitando…' : 'Quitar'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={startLiveRoom}
                            disabled={
                                !viewChainedSale?.max_duration_minutes ||
                                !viewChainedSale?.lobby_wait_minutes ||
                                !viewChainedSale?.products.length ||
                                startingLive
                            }
                        >
                            {startingLive ? 'Iniciando…' : 'Iniciar Subasta en Vivo'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={!!liveRoom} onClose={() => setLiveRoom(null)} fullWidth maxWidth="xs">
                <DialogTitle>Subasta en Vivo Iniciada</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Comparte este código con tu audiencia para que se unan a la sala.
                    </Typography>
                    <Typography
                        variant="h3"
                        align="center"
                        sx={{ fontWeight: 700, letterSpacing: '0.3em', color: 'primary.main', py: 2 }}
                    >
                        {liveRoom?.code}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLiveRoom(null)}>Cerrar</Button>
                    <Button
                        component={RouterLink}
                        to={liveRoom ? `/store/rooms/${liveRoom.id}` : '#'}
                        variant="contained"
                        color="primary"
                    >
                        Ver Sala en Vivo
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!shareMessage}
                autoHideDuration={4000}
                onClose={() => setShareMessage('')}
                message={shareMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </PageShell>
    );
}

export default StoreDashboard;
